/**
 * Service layer for Employee Options data fetching and mapping
 */

import { getEmpStatement, getMyStatement, getMyStatementForActivity, getEmpStatementForActivity } from '../../apis/options/MyOptions';
import { getEmployeeStatementForActivity } from '../../apis/options/Employee';
import { mapEmployeeOptionsStatement, mapEmployeeOptionsActivity } from '../mappers/employee-options-mapper';
import { EmployeeOptionsStatement } from '../types/employee-statement';
import { EmployeeOptionsApiResponse } from '../types/api/employee-options-api';

export class EmployeeOptionsService {
  /**
   * Fetch options data for the current user (respects server-side impersonation automatically)
   */
  static async getMyOptions(): Promise<EmployeeOptionsStatement> {
    try {
      const apiResponse: EmployeeOptionsApiResponse = await getMyStatement();

      // Sort options by issue date
      if (apiResponse.options) {
        apiResponse.options.sort((a, b) => new Date(a.issueDate).getTime() - new Date(b.issueDate).getTime());
      }

      return mapEmployeeOptionsStatement(apiResponse);
    } catch (error) {
      console.error('Error fetching my options:', error);
      throw error;
    }
  }

  /**
   * Fetch options data for a specific employee (manager viewing employee data)
   */
  static async getEmployeeOptions(employeeId: number): Promise<EmployeeOptionsStatement> {
    try {
      const apiResponse: EmployeeOptionsApiResponse = await getEmpStatement(employeeId);

      // Sort options by issue date
      if (apiResponse.options) {
        apiResponse.options.sort((a, b) => new Date(a.issueDate).getTime() - new Date(b.issueDate).getTime());
      }

      return mapEmployeeOptionsStatement(apiResponse);
    } catch (error) {
      console.error('Error fetching employee options:', error);
      throw error;
    }
  }

  /**
   * Fetch options data for Activity tab (no filtering, all option agreements)
   */
  static async getEmployeeOptionsForActivity(employeeId: number): Promise<EmployeeOptionsStatement> {
    try {
      const apiResponse: EmployeeOptionsApiResponse = await getEmployeeStatementForActivity(employeeId);

      // Sort options by issue date
      if (apiResponse.options) {
        apiResponse.options.sort((a, b) => new Date(a.issueDate).getTime() - new Date(b.issueDate).getTime());
      }

      return mapEmployeeOptionsActivity(apiResponse);
    } catch (error) {
      console.error('Error fetching employee options for activity:', error);
      throw error;
    }
  }

  /**
   * Fetch options data for Activity tab for current user (MyOptions)
   */
  static async getMyOptionsForActivity(): Promise<EmployeeOptionsStatement> {
    try {
      const apiResponse: EmployeeOptionsApiResponse = await getMyStatementForActivity();

      // Sort options by issue date
      if (apiResponse.options) {
        apiResponse.options.sort((a, b) => new Date(a.issueDate).getTime() - new Date(b.issueDate).getTime());
      }

      return mapEmployeeOptionsActivity(apiResponse);
    } catch (error) {
      console.error('Error fetching my options for activity:', error);
      throw error;
    }
  }

  /**
   * Fetch options data for Activity tab for a specific employee (Admin viewing via MyOptions endpoint)
   * Used for ghosting/admin scenarios
   */
  static async getEmployeeOptionsForActivityViaMyOptions(employeeId: number): Promise<EmployeeOptionsStatement> {
    try {
      const apiResponse: EmployeeOptionsApiResponse = await getEmpStatementForActivity(employeeId);

      // Sort options by issue date
      if (apiResponse.options) {
        apiResponse.options.sort((a, b) => new Date(a.issueDate).getTime() - new Date(b.issueDate).getTime());
      }

      return mapEmployeeOptionsActivity(apiResponse);
    } catch (error) {
      console.error('Error fetching employee options for activity via MyOptions:', error);
      throw error;
    }
  }
}